/**
 * Interceptor de fetch que injeta `X-Tenant-Id` em requests /api/*
 * quando o super_admin tem um cliente selecionado — TH-54656.
 *
 * Paridade com o OkHttp interceptor do mobile
 * (`provideTenantInterceptor` em
 * `apps/mobile-android/.../di/NetworkModule.kt`). Backend (TH-54654)
 * resolve o header para super_admin operando em campo; para usuários
 * tenant-scoped o header é ignorado.
 *
 * Por que monkey-patch do `window.fetch` em vez de helper:
 *   1. Todo o admin já usa `fetch("/api/...")` direto (~15 lugares).
 *   2. Migrar para um helper inflaria o PR e exigiria revisar cada
 *      chamada (matching options, credentials, headers).
 *   3. O filtro `url.startsWith("/api/")` evita afetar requests
 *      externos (assets do Vite, lucide icons, etc.).
 *
 * Idempotente: chamar `installTenantHeaderInterceptor()` mais de uma
 * vez (ex.: HMR) não duplica o wrap, porque guardamos o original em
 * `INSTALLED_MARK` e detectamos o re-install.
 */

import { getSelectedTenantId } from "@/stores/tenantSelectionStore";

const INSTALLED_MARK = Symbol.for("priceshow.admin.tenantHeaderInstalled");

type FetchFn = typeof window.fetch;
type MarkedFetch = FetchFn & { [INSTALLED_MARK]?: true };

export function installTenantHeaderInterceptor(): void {
  if ((window.fetch as MarkedFetch)[INSTALLED_MARK]) {
    return;
  }
  const original = window.fetch.bind(window);

  const wrapped: MarkedFetch = async (input, init) => {
    const url = resolveUrl(input);
    if (!url.startsWith("/api/")) {
      return original(input, init);
    }

    const tenantId = getSelectedTenantId();
    if (!tenantId) {
      return original(input, init);
    }

    const headers = new Headers(
      init?.headers ??
        (input instanceof Request ? input.headers : undefined),
    );
    headers.set("X-Tenant-Id", tenantId);
    return original(input, { ...(init ?? {}), headers });
  };
  wrapped[INSTALLED_MARK] = true;
  window.fetch = wrapped;
}

function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  if (input instanceof Request) return input.url;
  return String(input);
}
